"use client";
import { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { DeliveryFormValues } from "../lib/schemas/travel-booking";
import { deliverySchema } from "../lib/schemas/travel-booking";
import {
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
} from "@/components/ui/alert-dialog";
import Select from "react-select";
import { Option } from "components/Form/form";
import { FormField } from "../ui-form/form-field";
import { useQuery } from "react-query";
import { getAddressById } from "../apis";
import { useAppSelector } from "redux/hooks";

interface DeliveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (delivery: DeliveryFormValues) => void;
  initialData?: DeliveryFormValues;
  productOptions: any[];
  customers: any[];
}

// Mock data
const deliveryModes = [
  { value: "self", label: "Self" },
  { value: "courier", label: "Courier" },
  { value: "pickup", label: "Pickup" },
];

const countries = [
  { value: "us", label: "United States" },
  { value: "ca", label: "Canada" },
  { value: "uk", label: "United Kingdom" },
  { value: "in", label: "India" },
];

// Mock API call to get states by country
const fetchStatesByCountry = async (countryCode: string) => {
  // Simulate API call
  await new Promise((resolve) => setTimeout(resolve, 300));

  // Return mock states based on country
  const statesByCountry: Record<string, any[]> = {
    us: [
      { value: "ny", label: "New York" },
      { value: "ca", label: "California" },
      { value: "tx", label: "Texas" },
    ],
    ca: [
      { value: "on", label: "Ontario" },
      { value: "qc", label: "Quebec" },
      { value: "bc", label: "British Columbia" },
    ],
    uk: [
      { value: "eng", label: "England" },
      { value: "sct", label: "Scotland" },
      { value: "wls", label: "Wales" },
    ],
    in: [
      { value: "mh", label: "Maharashtra" },
      { value: "dl", label: "Delhi" },
      { value: "ka", label: "Karnataka" },
    ],
  };

  return statesByCountry[countryCode] || [];
};

const defaultDelivery: DeliveryFormValues = {
  deliveryMode: { value: "", label: "" },
  deliveryId: "",
  products: [],
  customers: { value: "", label: "" },
  deliveryType: "self",
  locationInfo: "",
  addresses: [
    {
      address1: "",
      address2: "",
      landmark: "",
      country: { value: "in", label: "India" },
      state: { value: "", label: "" },
      city: "",
      pincode: "",
    },
  ],
  address_id: "",
  local_id: [],
};

export function DeliveryModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  productOptions,
  customers,
}: DeliveryModalProps) {
  const [stateOptions, setStateOptions] = useState<any[]>([]);
  const {newGenricOrderID} = useAppSelector((state)=>state.CreateOrder)
  // Create a form specifically for this modal
  const form = useForm<DeliveryFormValues>({
    resolver: zodResolver(deliverySchema),
    defaultValues: initialData || defaultDelivery,
    mode: "onChange",
  });
  console.log(form.formState.errors);

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      form.reset(initialData);
    } else {
      form.reset(defaultDelivery);
    }
  }, [initialData, form]);

  const deliveryType = form.watch("deliveryType");
  const addresses = form.watch("addresses") || [];

  const handleDeliveryTypeChange = (value: "self" | "courier" | "pickup") => {
    form.setValue("deliveryType", value, { shouldValidate: true });
  };

  const handleAddressChange = async (
    index: number,
    field: string,
    value: any
  ) => {
    const updatedAddresses = [...addresses];
    updatedAddresses[index] = { ...updatedAddresses[index], [field]: value };
    form.setValue("addresses", updatedAddresses, { shouldValidate: true });
    if (field === "state") {
      updatedAddresses[index].state = value;
      form.setValue("addresses", updatedAddresses, { shouldValidate: true });
    }
    // If country changes, fetch states
    if (field === "country" && value?.value) {
      const states = await fetchStatesByCountry(value.value);
      setStateOptions(states);
    }
  };

  const addAddress = () => {
    form.setValue(
      "addresses",
      [
        ...addresses,
        {
          address1: "",
          address2: "",
          landmark: "",
          country: {
            value: "",
            label: "",
          },
          state: {
            value: "",
            label: "",
          },
          city: "",
          pincode: "",
        },
      ],
      { shouldValidate: true }
    );
  };

  const removeAddress = (index: number) => {
    const updatedAddresses = addresses.filter((_, i) => i !== index);
    form.setValue("addresses", updatedAddresses, { shouldValidate: true });
  };

  const handleSubmit = (values: DeliveryFormValues) => {
    onSubmit(values);
    if(initialData) {
      form.reset(initialData);
    } else {
      form.reset(defaultDelivery);
    }
  };

  // filter customers based on selected products
const filteredCustomers = (customers || newGenricOrderID)
  .filter((customer) => {
    const selectedProducts = form.getValues("products") || [];
    const customerProducts = customer.products || [];
    return selectedProducts.some((product) =>
      customerProducts.some((p: any) => (p?.local_id || p?.id) === product.value)
    );
  })
    .map((customer) => ({
      value: (customer?.customerId || customer?.value)|| newGenricOrderID?.[0]?.value,
      label: (customer?.fullName || customer?.label),
    }));

    // test again
  const customerId = Number(form.watch("customers")?.value);

  const selectAllProduct = () => {
    form.setValue("products", productOptions as Option[]);
    const allLocalIds = productOptions?.map((product) => product?.value);
    form.setValue("local_id", allLocalIds);
  };

  const {
    data: queryAddresses,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["addressByCustomerId", customerId],
    queryFn: () => getAddressById({ customers_id: customerId! }),
    enabled: !!customerId,
    refetchOnWindowFocus: false,
  });
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);

  const handleAddClick = () => {
    setIsFormOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!hasInitialized) {
      // Run only once on initial mount
      const hasAddresses =
        (Array.isArray(queryAddresses) && queryAddresses.length > 0) ||
        queryAddresses?.addresses?.length > 0 ||
        queryAddresses?.address_id?.length > 0;

      setIsFormOpen(!hasAddresses);
      setHasInitialized(true);
    }
  }, [queryAddresses, hasInitialized]);

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogPortal>
        <AlertDialogOverlay className=" bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
        <AlertDialogContent className="max-w-4xl p-0 rounded-lg bg-white-A700">
          <AlertDialogHeader className="flex text-left gap-4 py-6 px-10 border-b">
            {/* <AlertDialogTitle>{initialData ? "Edit Delivery" : "Add Delivery"}</AlertDialogTitle> */}
            <div className="flex flex-col">
              <h2 className="text-xl font-semibold text-gray-900">
                {initialData ? "Edit Delivery" : "Add Delivery"}
              </h2>
              <p className="text-sm text-left text-gray-500 mt-1">
                Fill the required details
              </p>
            </div>
          </AlertDialogHeader>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="space-y-4 px-10 overflow-auto max-h-[500px]">
                <div className="grid grid-cols-2 gap-5 pr-[38px]">
                  <FormField
                    form={form}
                    name="deliveryMode"
                    label="Delivery Mode"
                    render={({ field }) => (
                      <Select
                        options={deliveryModes}
                        value={field.value}
                        onChange={(option) =>
                          form.setValue("deliveryMode", option as Option, {
                            shouldValidate: true,
                          })
                        }
                        placeholder="Select delivery mode"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        components={{ IndicatorSeparator: () => null }}
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            padding: "8px",
                            borderRadius: "6px",
                            borderColor: state.isFocused
                              ? "#3b82f6"
                              : base.borderColor,
                            boxShadow: state.isFocused
                              ? "0 0 0 1px #3b82f6"
                              : base.boxShadow,
                            "&:hover": {
                              borderColor: "#3b82f6",
                            },
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            padding: "0 8px",
                          }),
                          singleValue: (base) => ({
                            ...base,
                            fontSize: "14px",
                            fontWeight: 500,
                          }),
                        }}                        
                      />
                    )}
                  />
                  <FormField
                    form={form}
                    name="deliveryId"
                    label="Delivery ID"                                                            
                    render={({ field }) => (
                      <input
                        {...field}
                        disabled={initialData && form.getValues('deliveryMode.value') !== 'courier'}
                        placeholder="Enter delivery ID"
                        className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                      />
                    )}
                  />
                  <FormField
                    form={form}
                    name="products"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex justify-between w-full p-0">
                          Products
                          <Button
                            onClick={selectAllProduct}
                            type="button"
                            variant="ghost"
                            disabled={!!initialData}
                            className="text-blue-700 p-1 h-auto"
                          >
                            Select All
                          </Button>
                        </FormLabel>
                        <FormControl>
                          <Select
                            options={productOptions}
                            value={field.value}
                            onChange={(options) => {
                              const selectedOptions = options || [];
                              const ids = selectedOptions.map(
                                (option) => option?.value
                              );
                              const uniqueIds = Array.from(new Set(ids));
                              form.setValue(
                                "products",
                                selectedOptions as Option[],
                                { shouldValidate: true }
                              );
                              form.setValue("local_id", uniqueIds);
                            }}
                            placeholder="Select products"
                            isMulti
                            className="react-select-container"
                            classNamePrefix="react-select"
                            components={{ IndicatorSeparator: () => null }}
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                padding: "8px",
                                borderRadius: "6px",
                                borderColor: state.isFocused
                                  ? "#3b82f6"
                                  : base.borderColor,
                                boxShadow: state.isFocused
                                  ? "0 0 0 1px #3b82f6"
                                  : base.boxShadow,
                                "&:hover": {
                                  borderColor: "#3b82f6",
                                },
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                padding: "0 8px",
                              }),
                              singleValue: (base) => ({
                                ...base,
                                fontSize: "14px",
                                fontWeight: 500,
                              }),
                            }}
                            isDisabled={!!initialData}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    form={form}
                    name="customers"
                    label="Customers"
                    render={({ field }) => (
                      <Select
                        options={filteredCustomers}
                        isDisabled={form.watch("products")?.length === 0 || !!initialData}
                        value={field.value}
                        onChange={(options) => {
                          form.setValue("customers", options as Option, {
                            shouldValidate: true,
                          });
                        }}
                        placeholder="Select customers"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        components={{ IndicatorSeparator: () => null }}
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            padding: "8px",
                            borderRadius: "6px",
                            borderColor: state.isFocused
                              ? "#3b82f6"
                              : base.borderColor,
                            boxShadow: state.isFocused
                              ? "0 0 0 1px #3b82f6"
                              : base.boxShadow,
                            "&:hover": {
                              borderColor: "#3b82f6",
                            },
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            padding: "0 8px",
                          }),
                          singleValue: (base) => ({
                            ...base,
                            fontSize: "14px",
                            fontWeight: 500,
                          }),
                        }}
                      />
                    )}
                  />
                </div>
                <div className="space-y-4 ">
                  <div className="flex justify-between items-center mt-10">
                    <FormLabel className="text-[#999999]">
                      Delivery Address*
                    </FormLabel>
                    <button
                      className="text-[#165FE3] text-[14px] font-semibold"
                      onClick={handleAddClick}
                      type="button"
                    >
                      Add
                    </button>
                  </div>
                  {isFormOpen &&
                    addresses?.map((address, index) => (
                      <div
                        key={index}
                        className="border-2 border-dotted border-gray-400 rounded-md p-4 space-y-3"
                      >
                        <div className="flex justify-between items-center">
                          {/* <h4 className="text-sm font-medium">
                          Address {index + 1}
                        </h4> */}
                          {index > 0 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeAddress(index)}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-5 text-[#2D3648]">
                            <FormField
                              form={form}
                              name={`addresses.${index}.address1` as any}
                              label="Address Line 1"
                              render={({ field }) => (
                                <input
                                  {...field}
                                  placeholder="Address line 1"
                                  className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[8px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                                />
                              )}
                            />
                            <FormField
                              form={form}
                              name={`addresses.${index}.address2` as any}
                              label="Address Line 2"
                              render={({ field }) => (
                                <input
                                  {...field}
                                  placeholder="Address line 2"
                                  className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[8px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                                />
                              )}
                            />
                            <FormField
                              form={form}
                              name={`addresses.${index}.landmark` as any}
                              label="Landmark"
                              render={({ field }) => (
                                <input
                                  {...field}
                                  placeholder="Landmark"
                                  className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[8px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                                />
                              )}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <FormField
                              form={form}
                              name={`addresses.${index}.country` as any}
                              label="Country"
                              render={({ field }) => (
                                <Select
                                  options={countries}
                                  isDisabled
                                  value={
                                    field.value
                                      ? countries.find(
                                          (option) =>
                                            option.value === field.value.value
                                        )
                                      : null
                                  }
                                  onChange={(option) =>
                                    handleAddressChange(
                                      index,
                                      "country",
                                      option
                                    )
                                  }
                                  placeholder="Select country"
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                  components={{
                                    IndicatorSeparator: () => null,
                                  }}
                                  styles={{
                                    control: (base, state) => ({
                                      ...base,
                                      padding: "8px",
                                      borderRadius: "6px",
                                      borderColor: state.isFocused
                                        ? "#3b82f6"
                                        : base.borderColor,
                                      boxShadow: state.isFocused
                                        ? "0 0 0 1px #3b82f6"
                                        : base.boxShadow,
                                      "&:hover": {
                                        borderColor: "#3b82f6",
                                      },
                                    }),
                                    valueContainer: (base) => ({
                                      ...base,
                                      padding: "0 8px",
                                    }),
                                    singleValue: (base) => ({
                                      ...base,
                                      fontSize: "14px",
                                      fontWeight: 500,
                                    }),
                                  }}
                                />
                              )}
                            />
                            <FormField
                              form={form}
                              name={`addresses.${index}.state` as any}
                              label="State"
                              render={({ field }) => (
                                // <Select
                                //   options={stateOptions}
                                //   value={
                                //     field.value
                                //       ? stateOptions.find((option) => option.value === field.value.value)
                                //       : null
                                //   }
                                //   onChange={(option) => handleAddressChange(index, "state", option)}
                                //   placeholder="Select state"
                                //   className="react-select-container"
                                //   classNamePrefix="react-select"
                                //   isDisabled={!address.country}
                                // />
                                <input
                                  {...field}
                                  value={field.value.value}
                                  onChange={(e) =>
                                    field.onChange({
                                      label: "",
                                      value: e.target.value,
                                    })
                                  }
                                  placeholder="State"
                                  placeholder="State"
                                  className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                                />
                              )}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <FormField
                              form={form}
                              name={`addresses.${index}.city` as any}
                              label="City"
                              render={({ field }) => (
                                <input
                                  {...field}
                                  placeholder="City"
                                  className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                                />
                              )}
                            />
                            <FormField
                              form={form}
                              name={`addresses.${index}.pincode` as any}
                              label="Pincode"
                              render={({ field }) => (
                                <input
                                  {...field}
                                  placeholder="Pincode"
                                  className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                                />
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  {form.formState.errors.addresses &&
                    !Array.isArray(form.formState.errors.addresses) && (
                      <p className="text-xs text-destructive mt-1">
                        {form.formState.errors.addresses.message}
                      </p>
                    )}
                </div>
                {true && (
                  <div className="">
                    {queryAddresses?.address_id && (
                      <div className="mt-6">
                        <h3 className="text-md font-medium mb-3">
                          Saved addresses
                        </h3>
                        <div className="grid grid-cols-3 md:grid-cols-2 gap-3">
                          {queryAddresses?.address_id?.map(
                            (address: any, index: number) => (
                              <button
                                key={index}
                                type="button"
                                disabled={!!initialData}
                                onClick={() => {
                                  // Store the address ID in the form
                                  if(form.getValues('address_id') === address?.id?.toString() || form.getValues('address_id') === index.toString()) {
                                    form.setValue("address_id",'');
                                    form.setValue("addresses.0.address1", '');
                                    form.setValue("addresses.0.address2", '');
                                    form.setValue("addresses.0.city", '');
                                    form.setValue("addresses.0.state", {
                                      value: '',
                                      label: '',
                                    });
                                    form.setValue("addresses.0.pincode", '');
                                    form.setValue("addresses.0.landmark", '');
                                    return;
                                  }
                                  form.setValue(
                                    "address_id",
                                    address?.id?.toString() || index.toString(),
                                    { shouldDirty: true }
                                  );

                                  // Populate the address form fields with the selected address data
                                  form.setValue(
                                    "addresses.0.address1",
                                    address?.house_number || "",
                                    { shouldDirty: true }
                                  );
                                  form.setValue(
                                    "addresses.0.address2",
                                    address?.street_number || "",
                                    { shouldDirty: true }
                                  );
                                  form.setValue(
                                    "addresses.0.city",
                                    address?.City || "",
                                    { shouldDirty: true }
                                  );
                                  form.setValue(
                                    "addresses.0.state",
                                    {
                                      value: address?.State || "",
                                      label: address?.State || "",
                                    },
                                    { shouldDirty: true }
                                  );
                                  form.setValue(
                                    "addresses.0.pincode",
                                    address?.pincode || "",
                                    { shouldDirty: true }
                                  );
                                  form.setValue(
                                    "addresses.0.landmark",
                                    address?.landmark || "",
                                    { shouldDirty: true }
                                  );
                                }}
                                className={`w-full text-left disabled:opacity-40 disabled:cursor-not-allowed ${
                                  form.getValues("address_id") ===
                                  (address?.id?.toString() || index.toString())
                                    ? "border border-blue-700 rounded-lg"
                                    : ""
                                }`}
                              >
                                <div
                                  className="bg-[#F3F7FF] rounded-xl px-5 py-3 space-y-1.5 transition-colors"
                                  key={index}
                                >
                                  <h2 className="text-[#2D3648] text-left font-medium leading-4 -tracking-[1%]">
                                    {form.getValues("customers")?.label}
                                  </h2>
                                  <div className="flex flex-col gap-1">
                                    <h3 className="text-black-label font-normal leading-4 -tracking-[1%]">
                                      {address?.house_number +
                                        " " +
                                        address?.street_number}
                                    </h3>
                                    <p className="text-[#666666] font-normal leading-4 -tracking-[1%]">
                                      {address?.City +
                                        ", " +
                                        address?.State +
                                        " " +
                                        address?.pincode}
                                    </p>
                                    {address?.landmark && (
                                      <p className="text-[#666666] text-sm font-normal leading-4 -tracking-[1%]">
                                        Landmark: {address?.landmark}
                                      </p>
                                    )}
                                  </div>
                                  {form.getValues("address_id") ===
                                    (address?.id?.toString() ||
                                      index.toString()) && (
                                    <div className="mt-2 flex items-center">
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="mr-1"
                                      >
                                        <path
                                          d="M8 0C3.584 0 0 3.584 0 8C0 12.416 3.584 16 8 16C12.416 16 16 12.416 16 8C16 3.584 12.416 0 8 0ZM6.4 12L2.4 8L3.528 6.872L6.4 9.736L12.472 3.664L13.6 4.8L6.4 12Z"
                                          fill="#165FE3"
                                        />
                                      </svg>
                                      <span className="text-blue-700 text-sm font-medium">
                                        Selected
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <AlertDialogFooter className="h-min flex items-center gap-2 flex-row py-5 px-10">
                <Button
                  className="text-blue-700 border border-blue-700 max-w-[80px] w-full"
                  type="button"
                  variant="outline"
                  onClick={() => {
                    form.reset(defaultDelivery);
                    onClose();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="border border-blue-700 text-white-A700 max-w-[80px] w-full bg-blue-700"
                  type="submit"
                >
                  {
                    initialData ? "Update" : "Save"
                  }
                </Button>
              </AlertDialogFooter>
            </form>
          </FormProvider>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
}
